import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CACHE_TTL, requestCache } from './request-cache';
import { Observable } from 'rxjs';
import { zonedTimeToUtc } from 'date-fns-tz';
import { ContractGroupEnum, SignalingTypeEnum, UserDutyEnum } from 'src/enumerators';
import { environment } from 'src/environments/environment';
import { Contract, ItemAdjustment } from 'src/models';
import {
  ContractAuxiliarItemBalanceHistory,
  ContractBalanceHistory,
  ContractDeviceItemBalanceHistory,
  ContractHorizontalItemBalanceHistory,
  ContractRetroreflectorization,
  ContractServiceItemBalanceHistory,
  ContractTrafficLightBalance,
  ContractVerticalItemBalanceHistory,
  ItemAdditive,
} from 'src/models/contract';
import { AuthService } from './auth.service';
import { RestApiService } from './rest-api.service';

interface IDistrict {
  nome: string;
}

interface ListWorkspaceUsersFilters {
  userDuty?: UserDutyEnum;
  registeredSignaling?: boolean;
  files?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ContractService extends RestApiService {
  private readonly baseUrl = environment.endpoint();

  // Endpoints
  private readonly endpoints = {
    contracts: '/contracts',
    contractsFull: '/contracts?full=true',
    films: '/road-sign-films',
    substrates: '/road-sign-substrates',
    markingPaints: '/road-sign-marking-paints',
    members: '/workspace-members',
    retroreflectorizations: '/contracts/retroreflectorizations',
    itemAdditive: '/contracts/items/additive/',
    itemsTemplate: '/contracts/items-template.xlsx',
  };

  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
  ) {
    super(_http);
  }

  listContracts(): Observable<Contract[]> {
    const url = `${this.baseUrl}${this.endpoints.contracts}`;
    return requestCache.get(url, CACHE_TTL.short, () => this._http.get<Contract[]>(url));
  }

  listFullContracts(): Observable<Contract[]> {
    const url = `${this.baseUrl}${this.endpoints.contractsFull}`;
    return requestCache.get(url, CACHE_TTL.short, () => this._http.get<Contract[]>(url));
  }

  // O histórico pedia o mesmo contrato uma vez por sinalização (e por placa).
  getContract(contractId: string): Observable<Contract> {
    const url = `${this.baseUrl}${this.endpoints.contracts}/${contractId}`;
    return requestCache.get(url, CACHE_TTL.short, () => this._http.get<Contract>(url));
  }

  createContract(body: Partial<Contract>): Observable<Contract> {
    return this._http.post<Contract>(`${this.baseUrl}${this.endpoints.contracts}`, body);
  }

  updateContract(contractId: string, body: Partial<Contract>): Observable<Contract> {
    return this._http.put<Contract>(`${this.baseUrl}${this.endpoints.contracts}/${contractId}`, body);
  }

  deleteContract(contractId: string): Observable<void> {
    return this._http.delete<void>(`${this.baseUrl}${this.endpoints.contracts}/${contractId}`);
  }

  submitContract(reqBody: any) {
    return this.createContract(reqBody);
  }

  removeContract(id: string) {
    return this.deleteContract(id);
  }

  // ============================================
  // WORKSPACE MEMBERS
  // ============================================

  listAllWorkspaceUsers(filters: ListWorkspaceUsersFilters = { files: true }) {
    const { userDuty, registeredSignaling, files = true } = filters;

    let params = new HttpParams().set('files', String(files));

    if (userDuty) {
      params = params.set('userDuty', userDuty);
    }
    if (registeredSignaling != null) {
      params = params.set('registeredSignaling', String(registeredSignaling));
    }

    const url = `${this.baseUrl}${this.endpoints.members}`;
    return requestCache.get(`${url}?${params.toString()}`, CACHE_TTL.short, () => this._http.get(url, { params }));
  }

  getWorkspaceMember(email: string) {
    const encoded = encodeURIComponent(email);
    const params = new HttpParams().set('email', encoded);
    return this._http.get(`${this.baseUrl}${this.endpoints.members}`, { params });
  }

  // ============================================
  // MATERIAIS (Films, Substrates, Paints)
  // ============================================

  getFilms() {
    const url = `${this.baseUrl}${this.endpoints.films}`;
    return requestCache.get(url, CACHE_TTL.static, () => this._http.get(url));
  }

  getSubstrates() {
    const url = `${this.baseUrl}${this.endpoints.substrates}`;
    return requestCache.get(url, CACHE_TTL.static, () => this._http.get(url));
  }

  getMarkingPaints() {
    const url = `${this.baseUrl}${this.endpoints.markingPaints}`;
    return requestCache.get(url, CACHE_TTL.static, () => this._http.get(url));
  }

  populateFilms() {
    return this.getFilms();
  }

  populateSubs() {
    return this.getSubstrates();
  }

  populateMarkPainting() {
    return this.getMarkingPaints();
  }

  // ============================================
  // LOCALIDADES (IBGE)
  // ============================================

  getStates() {
    return this._http.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
  }

  getCities(state: string) {
    return this._http.get<IDistrict[]>(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state}/distritos`,
    );
  }

  populateStates() {
    return this.getStates();
  }

  populateCities(state: string) {
    return this.getCities(state);
  }

  // ============================================
  // ARQUIVOS DO CONTRATO
  // ============================================

  uploadContractFile(files: Array<any>, contractId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/${contractId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  downloadFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/files/${fileId}`);
  }

  deleteFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/files/${fileId}`);
  }

  uploadContractImage(file: File, contractId: string) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this._http.post(
      `${this.baseUrl}/contracts/${contractId}/image`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  /** @deprecated Use uploadContractImage instead */
  uploadImages(file: File, contractId: string) {
    return this.uploadContractImage(file, contractId);
  }

  // ============================================
  // HISTÓRICO DE SALDO CONTRATUAL
  // ============================================

  getContractBalanceHistory(contractId: string) {
    return this._http.get<ContractBalanceHistory[]>(`${this.baseUrl}/contracts/${contractId}/balance`);
  }

  getContractVerticalItemBalanceHistory(itemId: string) {
    return this._http.get<ContractVerticalItemBalanceHistory[]>(
      `${this.baseUrl}/contracts/vertical-items/${itemId}/balance`,
    );
  }

  getContractHorizontalItemBalanceHistory(itemId: string) {
    return this._http.get<ContractHorizontalItemBalanceHistory[]>(
      `${this.baseUrl}/contracts/horizontal-items/${itemId}/balance`,
    );
  }

  getContractDeviceItemBalanceHistory(itemId: string) {
    return this._http.get<ContractDeviceItemBalanceHistory[]>(
      `${this.baseUrl}/contracts/device-items/${itemId}/balance`,
    );
  }

  getContractServiceItemBalanceHistory(itemId: string) {
    return this._http.get<ContractServiceItemBalanceHistory[]>(
      `${this.baseUrl}/contracts/service-items/${itemId}/balance`,
    );
  }

  getContractAuxiliarItemBalanceHistory(itemId: string) {
    return this._http.get<ContractAuxiliarItemBalanceHistory[]>(
      `${this.baseUrl}/contracts/auxiliar-items/${itemId}/balance`,
    );
  }

  /** @deprecated Use getContractAuxiliarItemBalanceHistory instead */
  getContracAuxiliarItemBalanceHistory(itemId: string) {
    return this.getContractAuxiliarItemBalanceHistory(itemId);
  }

  // ============================================
  // ITEMS DO CONTRATO
  // ============================================

  getContractVerticalItem(itemId: string) {
    return this._http.get(`${this.baseUrl}/contracts/vertical-items/${itemId}`);
  }

  getContractHorizontalItem(itemId: string) {
    return this._http.get(`${this.baseUrl}/contracts/horizontal-items/${itemId}`);
  }

  getContractDeviceItem(itemId: string) {
    return this._http.get(`${this.baseUrl}/contracts/device-items/${itemId}`);
  }

  getContractServiceItem(itemId: string) {
    return this._http.get(`${this.baseUrl}/contracts/service-items/${itemId}`);
  }

  // ============================================
  // ARQIUVOS DOS ITENS DO CONTRATO
  // ============================================

  // Vertical
  uploadVerticalItemFile(files: Array<any>, itemId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/vertical-items/${itemId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  getVerticalItemFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/vertical-items/files/${fileId}`);
  }

  deleteVerticalItemFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/vertical-items/files/${fileId}`);
  }

  postContractVerticalItemFile(files: Array<any>, itemId: string) {
    return this.uploadVerticalItemFile(files, itemId);
  }

  getContractVerticalItemFIle(fileId: string) {
    return this.getVerticalItemFile(fileId);
  }

  deleteContractVerticalItemFile(fileId: string) {
    return this.deleteVerticalItemFile(fileId);
  }

  // Horizontal
  uploadHorizontalItemFile(files: Array<any>, itemId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/horizontal-items/${itemId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  getHorizontalItemFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/horizontal-items/files/${fileId}`);
  }

  deleteHorizontalItemFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/horizontal-items/files/${fileId}`);
  }

  postContractHorizontalItemFile(files: Array<any>, itemId: string) {
    return this.uploadHorizontalItemFile(files, itemId);
  }

  getContractHorizontalItemFIle(fileId: string) {
    return this.getHorizontalItemFile(fileId);
  }

  deleteContractHorizontalItemFile(fileId: string) {
    return this.deleteHorizontalItemFile(fileId);
  }

  // Device
  uploadDeviceItemFile(files: Array<any>, itemId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/device-items/${itemId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  getDeviceItemFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/device-items/files/${fileId}`);
  }

  deleteDeviceItemFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/device-items/files/${fileId}`);
  }

  postContractDeviceItemFile(files: Array<any>, itemId: string) {
    return this.uploadDeviceItemFile(files, itemId);
  }

  getContractDeviceItemFIle(fileId: string) {
    return this.getDeviceItemFile(fileId);
  }

  deleteContractDeviceItemFile(fileId: string) {
    return this.deleteDeviceItemFile(fileId);
  }

  // Service
  uploadServiceItemFile(files: Array<any>, itemId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/service-items/${itemId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  getServiceItemFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/service-items/files/${fileId}`);
  }

  deleteServiceItemFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/service-items/files/${fileId}`);
  }

  postContractServiceItemFile(files: Array<any>, itemId: string) {
    return this.uploadServiceItemFile(files, itemId);
  }

  getContractServiceItemFIle(fileId: string) {
    return this.getServiceItemFile(fileId);
  }

  deleteContractServiceItemFile(fileId: string) {
    return this.deleteServiceItemFile(fileId);
  }

  // Auxiliar
  uploadAuxiliarItemFile(files: Array<any>, itemId: string) {
    const formData = this.createFormData(files);
    return this._http.post(
      `${this.baseUrl}/contracts/auxiliar-items/${itemId}/files`,
      formData,
      { headers: this.getFormDataHeaders() },
    );
  }

  getAuxiliarItemFile(fileId: string) {
    return this._http.get<{ url: string }>(`${this.baseUrl}/contracts/auxiliar-items/files/${fileId}`);
  }

  deleteAuxiliarItemFile(fileId: string) {
    return this._http.delete<{ url: string }>(`${this.baseUrl}/contracts/auxiliar-items/files/${fileId}`);
  }

  postContractAuxiliarItemFile(files: Array<any>, itemId: string) {
    return this.uploadAuxiliarItemFile(files, itemId);
  }

  getContractAuxiliarItemFIle(fileId: string) {
    return this.getAuxiliarItemFile(fileId);
  }

  deleteContractAuxiliarItemFile(fileId: string) {
    return this.deleteAuxiliarItemFile(fileId);
  }

  // ============================================
  // RETROREFLECTORIZAÇÃO DO CONTRATO
  // ============================================

  createContractRetro(retroData: ContractRetroreflectorization) {
    return this.post(this.endpoints.retroreflectorizations, retroData, ContractRetroreflectorization);
  }

  getContractRetro(contractId: string) {
    return this.get(`/contracts/${contractId}/retroreflectorization`, ContractRetroreflectorization);
  }

  updateContractRetro(retroData: ContractRetroreflectorization) {
    return this.put(`/contracts/retroreflectorizations/${retroData.id}`, retroData, ContractRetroreflectorization);
  }

  // ============================================
  // SALDO DE SEMAFÓRICA DO CONTRATO
  // ============================================

  createContractTrafficLightBalance(contractId: string, balance: ContractTrafficLightBalance) {
    return this._http.post(`${this.baseUrl}/contracts/${contractId}/traffic-light-balance`, balance);
  }

  getContractTrafficLightBalance(contractId: string) {
    return this._http.get(`${this.baseUrl}/contracts/${contractId}/traffic-light-balance`);
  }

  deleteContractTrafficLightBalance(balance: ContractTrafficLightBalance) {
    return this._http.delete(
      `${this.baseUrl}/contracts/${balance.contractId}/traffic-light-balance/${balance.id}`,
    );
  }

  postContractTrafficLightBalance(contractId: string, balance: ContractTrafficLightBalance) {
    return this.createContractTrafficLightBalance(contractId, balance);
  }

  removeContractTrafficLightBalance(balance: ContractTrafficLightBalance) {
    return this.deleteContractTrafficLightBalance(balance);
  }

  // ============================================
  // ADITIVOS DO ITEM DO CONTRATO
  // ============================================

  createContractItemAdditive(itemId: string, additive: ItemAdditive, type: ContractGroupEnum) {
    additive.itemId = itemId;
    additive.type = type;
    additive.baseDate = zonedTimeToUtc(new Date(additive.baseDate), 'America/Sao_Paulo');
    return this.post(this.endpoints.itemAdditive, additive, ItemAdditive);
  }

  deleteContractItemAdditive(additive: ItemAdditive) {
    return this.del(`${this.endpoints.itemAdditive}${additive.id}`, ItemAdditive);
  }

  postContractItemAdditive(itemId: string, additive: ItemAdditive, type: ContractGroupEnum) {
    return this.createContractItemAdditive(itemId, additive, type);
  }

  removeContractItemAdditive(additive: ItemAdditive, type: string) {
    return this.deleteContractItemAdditive(additive);
  }

  // ============================================
  // TEMPLATES
  // ============================================

  downloadContractItemsTemplateXlsx(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${this.endpoints.itemsTemplate}`, {
      responseType: 'blob' as 'json',
    }) as unknown as Observable<Blob>;
  }

  // ============================================
  // HELPERS 
  // ============================================

  checkForContractAdjustments(contract: Contract) {
    const applyAdjustments = (items: any[]) => {
      items?.forEach((item) => {
        const adjustment = this.getClosestAdjustment(item?.adjustments);
        if (adjustment) {
          item.unitValue = adjustment.unitValue;
        }
      });
    };

    contract?.verticalGroups?.forEach((group) => applyAdjustments(group.verticalItems));
    contract?.horizontalGroups?.forEach((group) => applyAdjustments(group.horizontalItems));
    contract?.deviceGroups?.forEach((group) => applyAdjustments(group.deviceItems));
    contract?.serviceGroups?.forEach((group) => applyAdjustments(group.serviceItems));
  }

  private getClosestAdjustment(adjustments: ItemAdjustment[]): ItemAdjustment | undefined {
    if (!adjustments?.length) return undefined;

    const now = new Date().getTime();

    return adjustments.reduce((closest, current) => {
      const currentDate = new Date(current?.baseDate).getTime();

      if (currentDate > now) return closest;

      if (!closest) return current;

      const closestDate = new Date(closest.baseDate).getTime();
      const currentDiff = Math.abs(now - currentDate);
      const closestDiff = Math.abs(now - closestDate);

      return currentDiff < closestDiff ? current : closest;
    }, undefined as ItemAdjustment | undefined);
  }

  getItemWithClosestDate(adjustments: ItemAdjustment[]) {
    return this.getClosestAdjustment(adjustments);
  }

  private getFormDataHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this._auth.getJwtToken()}`,
      FormData: 'true',
    });
  }

  private createFormData(files: Array<any>): FormData {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file.file);
    }
    return formData;
  }
}